import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { remove } from 'ramda';
import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Icon } from '@ui/icons/Icon.tsx';
import DraggableOptions from '@ui/inputs/subcomponents/DraggableOptions.tsx';

import { handleKeyboardSelect } from '../../../plugins/utils/keyboardUtils.ts';
import { useCSSColors } from '../../hooks/useCSSColors.ts';
import { isInIframe } from '../../utils/focus.ts';

const ItemWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1px;
  position: relative;
`;

const InputWrapper = styled.div<{ $first?: boolean; $last?: boolean }>`
  margin: 0 auto;
  width: 100%;
  padding-block: 4px;
  padding-inline: 12px 4px;
  position: relative;
  display: flex;
  box-sizing: border-box;
  background-color: var(--color-widgetText-10);
  line-height: 150%;
  min-height: 40px;
  align-items: center;
  border-radius: ${({ $first, $last }) => {
    if ($first) {
      return `8px 8px 0 0`;
    }
    if ($last) {
      return '0 0 8px 8px';
    }
  }};

  .-desktop & {
    padding-block: 6px;
    padding-inline: 6px 4px;
    min-height: 44px;
  }

  input {
    flex: 1 1 auto;
    min-width: 0;
    ${({ theme }) => theme.fontSize?.('16px')};
    line-height: 150%;
    font-weight: normal;
    color: var(--color-widgetText);
    border: 0;
    display: block;
    background-color: transparent;
    text-overflow: ellipsis;
    padding: 0;

    &::placeholder {
      color: var(--color-widgetText);
      opacity: 0.8;
    }

    &:focus:not([readonly]) {
      box-shadow: none;
      border-bottom: none;
    }

    &:focus {
      outline: none;
    }
  }

  div:last-child {
    position: relative;
  }
`;

const StopIconWrapper = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  margin-inline-end: 8px;
  justify-content: center;
  width: 28px;
  height: 28px;
`;

const StopNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 14px;
  height: 14px;
  border: 1px solid var(--color-primary);
  border-radius: 50%;
  background-color: var(--color-background);
  color: var(--color-primary);
  font-family: ${({ theme }) => theme.fonts.family};
  font-size: 9px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 7px;
`;

const ClosePositionHolder = styled.div`
  height: 32px;
  width: 32px;
`;
const IconWrapper = styled.div`
  cursor: pointer;
`;

const DraggableDiv = styled.div`
  touch-action: none;
`;
const DraggableIcon = styled(Icon)`
  cursor: grab;
`;

const Spacer = styled.div`
  width: 12px;
`;

const StyledInputTextWrapper = styled.div<{ $mobileHighZoomBreakpoint?: number }>`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;

  // for high zoom widths on mobile devices
  @media (max-width: ${props => `${props.$mobileHighZoomBreakpoint ?? 320}px`}) {
    display: unset;
    overflow-x: scroll;
  }
`;

type MenuItem = {
  iconId: string;
  label: string;
  onClick: () => void;
};

type SearchInputLabelledMultiProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'onChange' | 'value'> & {
  T: (key: string) => string;
  autoFocus?: boolean;
  className?: string;
  clickableItemOptions?: boolean;
  'data-testid'?: string;
  draggable?: boolean;
  first?: boolean;
  handleKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>, term: string, index: number) => void;
  handleMoveClick: (label: string) => void;
  handleRemoveClick: () => void;
  hasClearButton?: boolean;
  id: string;
  index: number;
  isMobile?: boolean;
  last?: boolean;
  mobileHighZoomBreakpoint?: number;
  onInputChange: (value: string) => void;
  onInputClearButtonClick?: (index: number) => void;
  onInputSelect?: (index: number) => void;
  placeholder?: string;
  searchInputs: unknown[];
  stopNumber?: number;
  value: string;
};

const SearchInputLabelledMulti = ({
  autoFocus = false,
  value,
  index,
  searchInputs,
  id,
  placeholder,
  onInputChange,
  onInputClearButtonClick,
  onInputSelect,
  handleKeyPress = () => {},
  className,
  clickableItemOptions,
  handleRemoveClick,
  handleMoveClick,
  draggable,
  T,
  'data-testid': dataTestId,
  hasClearButton = true,
  first,
  last,
  isMobile,
  mobileHighZoomBreakpoint,
  stopNumber = index,
  ...rest
}: SearchInputLabelledMultiProps) => {
  const colors = useCSSColors();
  const [term, setTerm] = useState('');

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id }); // This is important. Value passed to Id here needs to be unique and have the key 'id'.
  const style = {
    transition,
    transform: CSS.Transform.toString(transform), // Take the transform prop and apply it here
  };

  useEffect(() => {
    setTerm(value);
  }, [value]);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onInputSelect) {
      onInputSelect(index);
    } // set the currentIndex when we begin typing in the input field
    setTerm(e.target.value);
    onInputChange(e.target.value);
  };

  const handleClearClick = () => {
    if (onInputClearButtonClick) {
      onInputClearButtonClick(index);
    } // Calls resetInputData({ index })
    if (onInputSelect) {
      onInputSelect(index);
    }
  };

  useEffect(() => {
    // Don't auto-focus in iframe to prevent stealing focus from parent page
    if (autoFocus && inputRef.current && !isInIframe()) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    handleKeyPress(e, term, index);
  };

  const clearClick = () => {
    setTerm('');
    inputRef.current?.focus();
    handleClearClick();
  };

  const menuItems = [
    { iconId: 'move-to-start', label: 'Move to start', onClick: () => handleMoveClick('Move to start') },
    {
      iconId: 'move-up',
      label: 'Move up',
      onClick: () => {
        handleMoveClick('Move up');
      },
    },
    {
      iconId: 'move-down',
      label: 'Move down',
      onClick: () => {
        handleMoveClick('Move down');
      },
    },
    {
      iconId: 'move-to-end',
      label: 'Move to end',
      onClick: () => {
        handleMoveClick('Move to end');
      },
    },
    { iconId: 'remove', label: 'Remove stop', onClick: handleRemoveClick },
  ];

  const filterStopOptionsForIndex = (options: MenuItem[], currentInputs: unknown[], index: number) => {
    if (currentInputs.length === 3 && index === 1) {
      // its in the middle of a list of only three options
      return remove(1, 2, options);
    }
    if (index === 0) {
      // its at the top of the list
      return remove(0, 2, options);
    }
    if (index === 1) {
      // its second to the top of the list
      return remove(1, 1, options);
    }
    if (index === currentInputs.length - 2) {
      // its second to the bottom of the list
      return remove(2, 1, options);
    }
    if (index === currentInputs.length - 1) {
      // its at the bottom of the list
      return remove(2, 2, options);
    }
    return options; // its in the middle of the list
  };

  const filteredOptions = filterStopOptionsForIndex(menuItems, searchInputs, index);

  // on each render, we do a quick DOM search to see if the search results are showing
  // DOM searching can be slow, but getElementById is fast enough for this purpose
  const resultId = `Stop${index}SuggestedLocations`;
  const isSearchResultsShowing = Boolean(document.getElementById(resultId));

  return (
    <ItemWrapper ref={setNodeRef} style={style} role={'group'} aria-label={placeholder}>
      <InputWrapper data-testid={dataTestId} className={className} $first={first} $last={last}>
        <StopIconWrapper>
          {first || last ? (
            <Icon
              name={last ? 'location' : 'starting.point'}
              cdn
              fillColor={colors.primaryButton}
              width={28}
              height={28}
            />
          ) : (
            <StopNumber data-testid="StopNumber">{stopNumber}</StopNumber>
          )}
        </StopIconWrapper>
        <StyledInputTextWrapper
          data-testid={'SearchInputTextWrapper'}
          $mobileHighZoomBreakpoint={mobileHighZoomBreakpoint}
        >
          <input
            id={id}
            ref={inputRef}
            {...rest}
            value={term}
            onChange={handleUpdate}
            onKeyDown={handleKey}
            placeholder={placeholder}
            aria-autocomplete="list"
            aria-haspopup={false}
            aria-controls={isSearchResultsShowing ? resultId : undefined}
          />
        </StyledInputTextWrapper>

        <ClosePositionHolder>
          {hasClearButton && term.length ? (
            <IconWrapper
              role={'button'}
              className="closeButton"
              aria-label={T('Clear field')}
              onClick={clearClick}
              data-testid="ClearButton"
              tabIndex={0}
              onKeyDown={handleKeyboardSelect(() => clearClick())}
            >
              <Icon name="delete-text" cdn fillColor={colors.secondaryText} width={32} height={32} />
            </IconWrapper>
          ) : null}
        </ClosePositionHolder>
        {draggable && (
          <DraggableDiv
            data-testid="DraggableDiv"
            aria-label={T(`Move field`)}
            {...attributes}
            {...listeners}
            aria-roledescription={undefined}
          >
            <DraggableIcon name="drag-2" cdn fillColor={colors.secondaryText} width={32} height={32} />
          </DraggableDiv>
        )}
      </InputWrapper>
      {clickableItemOptions && (
        <>
          <Spacer />
          <DraggableOptions menuItems={filteredOptions} isMobile={isMobile} T={T} />
        </>
      )}
    </ItemWrapper>
  );
};

export default SearchInputLabelledMulti;
